import * as React from 'react'

import { Box, Button, Title } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { factorialFormFromReview } from 'modules/reviews/components/ReviewSettings/CycleConfiguration/CycleConfiguration'
import RemindParticipantsModal from 'modules/reviews/components/Show/RemindParticipantsModal'
import RestartReviewModal from 'modules/reviews/components/Show/RestartReviewModal'
import StartReviewModal from 'modules/reviews/components/Show/StartReviewModal'
import StopReviewModal from 'modules/reviews/components/Show/StopReviewModal'
import { tagColor, tagLabel } from 'modules/reviews/lib/tagConstants'
import { useReviewShowContext } from 'modules/reviews/pages/performance/Show/hooks'
import { Status } from 'modules/reviews/store/models/v2/Review'

import ReviewShowPageTabs from './ReviewShowPageTabs'

interface Props {
  reviewId: number
  children?: React.ReactNode
}

function ReviewShowWrapper({ reviewId, children }: Props) {
  const { review, canEditReview, updateCanBeStarted, canbeStarted } = useReviewShowContext()
  const { loading: loadingCanBeStarted } = useAsyncData(async () => {
    if (canEditReview) updateCanBeStarted()
  }, [reviewId])

  const reviewStatus = review.attributes.status
  const color = tagColor(reviewStatus)
  const label = tagLabel(reviewStatus)
  const [showRemindParticipantsModal, setShowRemindParticipantsModal] = React.useState(false)
  const [showStopReviewModal, setShowStopReviewModal] = React.useState(false)
  const [showRestartReviewModal, setShowRestartReviewModal] = React.useState(false)
  const [showStartReviewModal, setShowStartReviewModal] = React.useState(false)

  const handlePrimaryActionClose = async () => {
    setShowStopReviewModal(false)
    await updateCanBeStarted()
  }

  const handleStartClick = () => {
    track(trackingEvents.reviews.startReviewStart)
    setShowStartReviewModal(true)
  }

  const PrimaryAction = () => {
    switch (reviewStatus) {
      case Status.scheduled:
      case Status.active:
        return (
          <Button iconName="stop" onClick={() => setShowStopReviewModal(true)}>
            {i18n.t('surveys.review.settings.stop.action')}
          </Button>
        )

      case Status.draft:
        return (
          <Button iconName="play" onClick={() => handleStartClick()} disabled={!canbeStarted}>
            {i18n.t('surveys.review.settings.start.action')}
          </Button>
        )

      case Status.finished:
        return (
          <Button
            iconName="play"
            onClick={() => setShowRestartReviewModal(true)}
            disabled={!canbeStarted}
          >
            {i18n.t('surveys.review.settings.restart.action')}
          </Button>
        )

      default:
        return null
    }
  }

  const Actions = () => {
    if (!canEditReview) return null

    return (
      <Box flexDirection="row" gap="s8">
        <PrimaryAction />
        {reviewStatus === 'active' && (
          <PopoverMenu type="list">
            <PopoverMenu.Trigger>
              <Button.Decorative type="secondary" iconName="ellipsis" />
            </PopoverMenu.Trigger>
            <PopoverMenu.Content side="bottom" align="center" hideWhenDetached={false}>
              <PopoverMenu.TouchableOption
                title={i18n.t('surveys.remind_participants')}
                onClick={() => setShowRemindParticipantsModal(true)}
              />
            </PopoverMenu.Content>
          </PopoverMenu>
        )}
      </Box>
    )
  }

  return (
    <ResponsivePage.Body loading={loadingCanBeStarted} error={!review} showHeader={false}>
      <Box gap="s16">
        <Title title={review.name} tag={{ label: label, type: color, showCircle: true }}>
          <Box flexDirection="row" gap="s16" alignItems="center">
            <Actions />
          </Box>
        </Title>
        <Box gap="s48">
          <ReviewShowPageTabs form={review} canEditReview={canEditReview} />
          {children}
        </Box>
        {showRemindParticipantsModal && (
          <RemindParticipantsModal
            review={review}
            onClose={() => setShowRemindParticipantsModal(false)}
          />
        )}
        {showStartReviewModal && (
          <StartReviewModal
            review={review}
            onClose={() => setShowStartReviewModal(false)}
            reviewData={factorialFormFromReview(review)}
          />
        )}
        {showStopReviewModal && (
          <StopReviewModal review={review} onClose={handlePrimaryActionClose} />
        )}
        {showRestartReviewModal && (
          <RestartReviewModal review={review} onClose={() => setShowRestartReviewModal(false)} />
        )}
      </Box>
    </ResponsivePage.Body>
  )
}

export default observer(ReviewShowWrapper)
